import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import { isDeviceForbiddenError } from '../../../lib/deviceAuthError'

export interface AwardStampsInput {
  memberId: string
  pin: string
  purchaseAmount?: number
  mealsCount?: number
  receiptReference?: string
}

export interface AwardStampsResult {
  stampsAdded: number
  newStampTotal: number
  stampTarget: number
  rewardsUnlocked: number
  rewardName: { en: string; sv: string } | null
}

interface AwardStampsResponse {
  success?: boolean
  error?: string
  stampsAdded?: number
  newStampTotal?: number
  stampTarget?: number
  rewardsUnlocked?: number
  rewardName?: { en: string; sv: string } | null
}

export class InvalidCashierPinError extends Error {
  constructor() {
    super('invalid_pin')
  }
}

async function awardStamps(input: AwardStampsInput): Promise<AwardStampsResult> {
  const { data, error } = await supabase.functions.invoke<AwardStampsResponse>('award-stamps', {
    body: {
      memberId:         input.memberId,
      pin:              input.pin,
      purchaseAmount:   input.purchaseAmount,
      mealsCount:       input.mealsCount,
      receiptReference: input.receiptReference,
    },
  })

  if (data?.error === 'invalid_pin') throw new InvalidCashierPinError()
  if (error || !data?.success) {
    const failure = error ?? new Error('award_stamps_failed')
    if (!isDeviceForbiddenError(failure)) reportUnexpectedError(failure)
    throw failure
  }

  return {
    stampsAdded:     data.stampsAdded!,
    newStampTotal:   data.newStampTotal!,
    stampTarget:     data.stampTarget!,
    rewardsUnlocked: data.rewardsUnlocked!,
    rewardName:      data.rewardName ?? null,
  }
}

export function useAwardStamps() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: awardStamps,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['member-stamp-data', variables.memberId] })
    },
  })
}
